import React, { useEffect, useRef, useState } from 'react';
import SlidePanel from '../../shared/SlidePanel';
import { when } from '../bailey/format';
import { hapticTick } from '../../../_utils/haptics';
import { Transcript } from './ConversationPanels';
import { dialable } from './api';
import { describeBooking, bookLabel, groupTitle, nextStep, reachOf } from './needs';
import { consultLabel, useNamedBooking } from './bookIt';

/**
 * Everything about one person waiting on the salon: what Bailey has ready,
 * the messages it took, and the conversation they came from, word for word.
 * Done on the left, the next step on the right.
 */
export default function MessagePanel({ group: g, fd, busy, onBook, onConsult, onReply, onAnswer, onDone, onClose }) {
    const sheet = useRef(null);
    const after = useRef(null);
    const [sid, setSid] = useState(g.sessionIds[0] || null);
    const [looking, setLooking] = useState('');   // '' | 'loading' | 'missing'
    const step = nextStep(g, fd.conversationsById);
    const b = useNamedBooking(step.kind === 'book' ? step.booking : null);
    const conversation = sid ? fd.conversationsById[sid] : null;
    const found = !!conversation;

    // Not loaded yet: look back through the pages for it.
    useEffect(() => {
        if (!sid || found) { setLooking(''); return undefined; }
        let live = true;
        setLooking('loading');
        fd.findConversation(sid).then((c) => { if (live) setLooking(c ? '' : 'missing'); });
        return () => { live = false; };
    }, [sid, found]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close with the panel's own animation, then do what was asked.
    const closeThen = (fn) => { after.current = fn; if (sheet.current) sheet.current.handleClose(); else { onClose(); fn(); } };
    const closed = () => { onClose(); const fn = after.current; after.current = null; if (fn) fn(); };

    const canReply = !!g.email || (!!g.phone && g.question);
    const buttons = [
        g.phone && { key: 'call', primary: step.kind === 'call', el: (cls) => <a key="call" className={cls} href={`tel:${dialable(g.phone)}`} onClick={() => hapticTick()}>Call</a> },
        canReply && { key: 'reply', primary: step.kind === 'reply' || step.kind === 'email', el: (cls) => <button key="reply" type="button" className={cls} onClick={() => { hapticTick(); closeThen(() => onReply(g)); }}>Reply</button> },
        step.kind === 'answer' && { key: 'answer', primary: true, el: (cls) => <button key="answer" type="button" className={cls} onClick={() => { hapticTick(); closeThen(() => onAnswer(g)); }}>Add the answer</button> }
    ].filter(Boolean).sort((a, b) => Number(a.primary) - Number(b.primary));

    const footer = (
        <div className="fd-panel-footer">
            <button type="button" className="sp-btn-secondary" onClick={() => { hapticTick(); closeThen(() => onDone(g)); }}>Done</button>
            {buttons.map((b) => b.el(b.primary ? 'sp-btn-primary' : 'sp-btn-secondary'))}
        </div>
    );
    const reach = reachOf(g);

    return (
        <SlidePanel ref={sheet} title={groupTitle(g, step)} onClose={closed} width={480} className="bly-panel fd-reader fd-person" footer={footer}>
            <div className="bly-panel-body fd-transcript">
                <p className="fd-transcript-meta">{reach || 'They didn’t leave a number or email.'}</p>

                {b && (
                    <div className="fd-ready">
                        <span className="fd-ready-label">Ready for you</span>
                        <b>{describeBooking(b)}</b>
                        <p>{readyNote(b)}</p>
                        <div className="bly-actions">
                            {b.consult && (
                                <button type="button" className="bly-btn is-secondary" disabled={!!busy} onClick={() => onConsult(g, b)}>
                                    {busy === 'consult' ? 'Getting it ready…' : consultLabel(b)}
                                </button>
                            )}
                            <button type="button" className="bly-btn is-primary" disabled={!!busy} onClick={() => onBook(g, b)}>
                                {busy === 'book' ? 'Getting it ready…' : bookLabel(b)}
                            </button>
                        </div>
                    </div>
                )}

                <div className="bly-panel-group">
                    <h4>{g.count === 1 ? 'The message I took' : `The ${g.count} messages I took`}</h4>
                    <div className="fd-msgs">
                        {g.messages.map((m) => (
                            <div key={m.id} className="fd-msg">
                                <p>{m.text || 'No message.'}</p>
                                <small>
                                    {when(m.at)}
                                    {g.sessionIds.length > 1 && m.sessionId && m.sessionId !== sid && (
                                        <button type="button" className="bly-link" onClick={() => { hapticTick(); setSid(m.sessionId); }}>Read this conversation</button>
                                    )}
                                </small>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bly-panel-group" aria-live="polite">
                    <h4>{g.sessionIds.length > 1 ? `The conversation (${g.sessionIds.indexOf(sid) + 1} of ${g.sessionIds.length})` : 'The conversation'}</h4>
                    {!sid && <p className="bly-panel-note">This message didn’t come with its conversation.</p>}
                    {sid && looking === 'loading' && !found && <p className="bly-panel-note">Opening the conversation…</p>}
                    {sid && looking === 'missing' && !found && <p className="bly-panel-note">I couldn’t find this conversation. It’s further back than I can look for now.</p>}
                    {found && <Transcript conversation={conversation} />}
                </div>
            </div>
        </SlidePanel>
    );
}

/** Why the booking is waiting, in Bailey's words. */
function readyNote(b) {
    if (b.blocked) {
        const why = b.consult ? (/patch/i.test(b.rule || '') ? 'It needs a patch test first' : 'It needs a consultation first') : 'It couldn’t be booked in the chat';
        return `${why}, so I sent them to your booking page. If you’re happy to go ahead, this fills in your diary for you to save.`;
    }
    return 'I sent them a link to confirm it online. If they haven’t yet, this fills in your diary for you to save.';
}
